import { safeSessionStorage } from '../lib/safeStorage';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

export const GOOGLE_VERIFIER_KEY = 'mp_google_verifier';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const base64url = (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export async function beginGoogle(role) {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const binding = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
  if (!safeSessionStorage.setItem(GOOGLE_VERIFIER_KEY, verifier)) throw new Error("Google girişi için tarayıcı depolamasına izin ver veya e-posta ile giriş yap.");
  const params = new URLSearchParams({ binding });
  if (role) params.set('role', role);
  window.location.assign(`${BACKEND}/api/auth/google/start?${params}`);
}

export default function GoogleSignIn({ role, disabled = false }) {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [checking, setChecking] = useState(true);
  const [retry, setRetry] = useState(0);
  const submitting = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const restore = () => { submitting.current = false; setBusy(false); setError(''); };
    window.addEventListener('pageshow', restore);
    return () => { mounted.current = false; window.removeEventListener('pageshow', restore); };
  }, []);

  useEffect(() => {
    let active = true;
    setChecking(true); setStatusError(false);
    axios.get(`${BACKEND}/api/auth/google/status`, { timeout: 10000 }).then(({ data }) => {
      if (!active) return;
      if (typeof data?.available !== 'boolean') throw new Error('Invalid availability response');
      setAvailable(data.available);
    }).catch(() => {
      if (active) { setAvailable(false); setStatusError(true); }
    }).finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [retry]);

  if (checking) return <p className="mt-4 text-sm text-zinc-600" role="status">Google girişi kontrol ediliyor…</p>;
  if (statusError) return <div className="mt-4 text-sm">
    <p role="status">Google girişine ulaşılamadı. Tekrar deneyebilir veya e-posta ve şifrenle giriş yapabilirsin.</p>
    <button type="button" disabled={disabled} className="mt-2 text-[#1E3A8A] underline disabled:opacity-50"
      onClick={() => setRetry(value => value + 1)}>Google girişini tekrar kontrol et</button>
  </div>;
  if (!available) return null;
  return <div className="mt-4">
    <button type="button" disabled={disabled || busy} className="google-sign-in" aria-busy={busy}
      onClick={async () => {
        if (submitting.current) return;
        submitting.current = true; setBusy(true); setError('');
        try { await beginGoogle(role); }
        catch (e) {
          submitting.current = false;
          if (mounted.current) {
            setError(e instanceof TypeError ? 'Google girişi başlatılamadı. Sayfayı güncel Chrome veya Safari tarayıcısında açıp yeniden dene.'
              : e.message || 'Google girişi başlatılamadı. Lütfen tekrar deneyin.');
            setBusy(false);
          }
        }
      }}><svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" className="google-sign-in-mark">
        <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.36Z" />
        <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.41l-3.24-2.51c-.9.6-2.04.96-3.38.96-2.6 0-4.81-1.76-5.6-4.12H3.06v2.59A10 10 0 0 0 12 22Z" />
        <path fill="#FBBC05" d="M6.4 13.92a6 6 0 0 1 0-3.84V7.49H3.06a10 10 0 0 0 0 9.02l3.34-2.59Z" />
        <path fill="#EA4335" d="M12 5.96c1.47 0 2.79.5 3.82 1.5l2.87-2.87A9.6 9.6 0 0 0 12 2a10 10 0 0 0-8.94 5.49l3.34 2.59A6 6 0 0 1 12 5.96Z" />
      </svg><span>{busy ? 'Google’a yönlendiriliyor…' : 'Google ile devam et'}</span></button>
    {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
  </div>;
}
